<template>
  <GenericModal
    :show="show"
    :title="modalTitle"
    :message="modalMessage"
    :confirm-text="modalConfirmText"
    :cancel-text="cancelText"
    :confirm-variant="confirmVariant"
    :cancel-variant="cancelVariant"
    @confirm="handleConfirm"
    @cancel="handleCancel"
    @update:show="$emit('update:show', $event)"
  >
    <!-- Partnership Benefits Content -->
    <div v-if="type === 'partnership'" class="partnership-content">
      <div class="mb-4" style="color: #191919; font-size: 16px; line-height: 1.5">
        You've selected <strong>{{ courseDisplayName }}</strong
        >, which is also supported as part of your program.
      </div>

      <div class="mb-4" style="color: #191919; font-size: 16px; line-height: 1.5">
        By clicking "Yes, Add Course," you acknowledge and agree that you are responsible for the associated fees.
      </div>

      <!-- Fee Structure -->
      <div class="fee-structure">
        <!-- Enrollment Fee -->
        <div class="d-flex align-center mb-4">
          <div class="mr-4 flex-shrink-0">
            <div
              class="d-flex align-center justify-center rounded-circle pa-3"
              style="width: 48px; height: 48px; background: var(--asu-icon-background-light)"
            >
              <img src="@/assets/icons/book.svg" alt="Book icon" style="width: 21px; height: auto" />
            </div>
          </div>
          <div>
            <div class="d-flex align-center ga-2 mb-1">
              <span
                class="font-weight-bold"
                style="color: #8c1d40; font-size: 20px; letter-spacing: -0.015em; line-height: 1.3"
              >
                $25
              </span>
              <span
                class="font-weight-bold"
                style="color: #000000; font-size: 20px; letter-spacing: -0.015em; line-height: 1.3"
              >
                enrollment fee:
              </span>
            </div>
            <div style="color: #191919; font-size: 14px; line-height: 1.286">
              Reserve your spot in the course and begin coursework.
            </div>
          </div>
        </div>

        <!-- Credit Conversion Fee -->
        <div class="d-flex align-center">
          <div class="mr-4 flex-shrink-0">
            <div
              class="d-flex align-center justify-center rounded-circle pa-3"
              style="width: 48px; height: 48px; background: var(--asu-icon-background-light)"
            >
              <img
                src="@/assets/icons/graduation-cap-icon.svg"
                alt="Graduation cap icon"
                style="width: 24px; height: auto"
              />
            </div>
          </div>
          <div>
            <div class="d-flex align-center ga-2 mb-1">
              <span
                class="font-weight-bold"
                style="color: #8c1d40; font-size: 20px; letter-spacing: -0.015em; line-height: 1.3"
              >
                $400
              </span>
              <span
                class="font-weight-bold"
                style="color: #000000; font-size: 20px; letter-spacing: -0.015em; line-height: 1.3"
              >
                credit conversion fee:
              </span>
            </div>
            <div style="color: #191919; font-size: 14px; line-height: 1.286">
              Pay only if you're satisfied with your grade and want to add it to your transcript.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Default slot for other content -->
    <slot v-else />
  </GenericModal>
</template>

<script>
import { defineComponent } from 'vue';
import GenericModal from './GenericModal.vue';

export default defineComponent({
  name: 'CourseEnrollmentModal',

  components: {
    GenericModal
  },

  props: {
    show: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: 'partnership',
      validator: (value) => ['partnership', 'confirmation', 'warning'].includes(value)
    },
    course: {
      type: Object,
      default: () => ({})
    },
    title: {
      type: String,
      default: ''
    },
    message: {
      type: String,
      default: ''
    },
    confirmText: {
      type: String,
      default: 'Continue'
    },
    cancelText: {
      type: String,
      default: 'Cancel'
    },
    confirmVariant: {
      type: String,
      default: 'gold'
    },
    cancelVariant: {
      type: String,
      default: 'dark-outline'
    }
  },

  emits: ['update:show', 'confirm', 'cancel'],

  computed: {
    courseDisplayName() {
      if (!this.course) return '[Selected Course]';

      // Use coursePrefix and courseName from API structure
      const prefix = this.course.coursePrefix || this.course.prefix || '';
      const name = this.course.courseName || this.course.name || this.course.title || '';

      if (prefix && name) {
        return `${prefix} - ${name}`;
      } else if (name) {
        return name;
      } else if (prefix) {
        return prefix;
      }

      return '[Selected Course]';
    },

    modalTitle() {
      if (this.title) return this.title;

      const titles = {
        partnership: this.getPartnershipTitle(),
        confirmation: 'Confirm Enrollment',
        warning: 'Enrollment Warning'
      };
      return titles[this.type] || 'Course Enrollment';
    },

    modalMessage() {
      if (this.message) return this.message;

      // For partnership type, we don't show the generic message since we have custom content
      if (this.type === 'partnership') return '';

      const messages = {
        confirmation: `Are you sure you want to enroll in ${this.course.title || 'this course'}?`,
        warning: 'Please review the enrollment details before proceeding.'
      };
      return messages[this.type] || 'Please confirm your enrollment.';
    },

    modalConfirmText() {
      if (this.confirmText !== 'Continue') return this.confirmText;

      const confirmTexts = {
        partnership: 'Yes, add course',
        confirmation: 'Enroll Now',
        warning: 'Continue'
      };
      return confirmTexts[this.type] || 'Continue';
    }
  },

  methods: {
    getPartnershipTitle() {
      // Check if the course is session-based (has specific term dates) or on-demand
      const isOnDemand =
        this.course?.term?.sessionName === 'On-demand' ||
        this.course?.term?.name === 'On-demand' ||
        this.course?.lengthCategory === 'On-demand';

      if (isOnDemand) {
        return 'Add an on-demand course';
      } else {
        return 'Add a session-based course';
      }
    },

    handleConfirm() {
      this.$emit('confirm', {
        course: this.course,
        type: this.type
      });
    },

    handleCancel() {
      this.$emit('cancel', {
        course: this.course,
        type: this.type
      });
    }
  }
});
</script>
